import { inject, Injectable } from '@angular/core';
import {
    DocumentWorkflowStep,
    Envelope,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import { DialogMode } from '../../../../../../models/dialog.model';
import { DialogService } from '../../../../../../services/dialog.service';
import { ModalManagementService } from '@wdx/clmi/ui';
import { EnvelopeFacadeService } from '../../../../../../state/envelope/envelope-facade.service';
import { CommentDialogComponent } from '../../../../comments/comment-dialog/comment-dialog.component';
import {
    DocumentChecklistAction,
    DocumentChecklistItem,
} from '../../../document-checklist/document-checklist.model';
import {
    ActionTrigger,
    ADD_TO_ENVELOPE_ACTION,
    APPROVE_ACTION,
    CANCEL_ACTION,
    CREATE_EXTERNAL_TASK,
    GENERATE_DOCUMENT_ACTION,
    MARK_AS_PROVIDED_ACTION,
    REJECT_ACTION,
    UPLOAD_ACTION,
} from '../../../document-checklist/document-checklist.static';
import { DocumentsFacadeService } from '../documents-facade';
import { DocumentsModalService } from '../documents-modal';
import {
    TRANSLATION_DIALOG_COMMENT_BODY_REJECT,
    TRANSLATION_DIALOG_MENTION_PARTIES,
    TRANSLATION_DIALOG_TITLE_REJECT_ITEM,
    TranslationsService,
} from '@wdx/shared/utils';

@Injectable({
    providedIn: 'root',
})
export class DocumentChecklistActionsService {
    activeDocumentChecklistItems: DocumentChecklistItem[];
    private documentsModalService = inject(DocumentsModalService);
    private documentsFacadeService = inject(DocumentsFacadeService);
    private envelopeFacadeService = inject(EnvelopeFacadeService);
    private dialogService = inject(DialogService);
    private modalManagementService = inject(ModalManagementService);
    private translationsService = inject(TranslationsService);

    getActions(
        workflowStep: DocumentWorkflowStep,
        hasPartyId = false,
    ): ActionTrigger[] {
        return (workflowStep?.actions || [])
            .map((action) => {
                switch (action.code) {
                    case DocumentChecklistAction.Approve:
                        return APPROVE_ACTION;
                    case DocumentChecklistAction.Generate:
                        return GENERATE_DOCUMENT_ACTION;
                    case DocumentChecklistAction.MarkAsProvided:
                        return MARK_AS_PROVIDED_ACTION;
                    case DocumentChecklistAction.Reject:
                        return REJECT_ACTION;
                    case DocumentChecklistAction.Upload:
                        return UPLOAD_ACTION;
                    case DocumentChecklistAction.Cancel:
                        return CANCEL_ACTION;
                    case DocumentChecklistAction.CreateExternalTask:
                        return hasPartyId ? CREATE_EXTERNAL_TASK : null;
                    default:
                        break;
                }
            })
            .filter(Boolean);
    }

    /**
     * Triggers a checklist action against provided requirement items
     * @param action
     * @param items
     * @param entityType
     * @param entityId
     */
    doAction(
        action: DocumentChecklistAction,
        items: DocumentChecklistItem[],
        entityType: SystemEntity,
        entityId: string,
    ): void {
        const documentRequirementIds = items.map(
            (item) => item.documentRequirement.id,
        );
        switch (action) {
            case DocumentChecklistAction.AddToEnvelope:
                this.activeDocumentChecklistItems = items;
                break;
            case DocumentChecklistAction.Generate:
                this.documentsModalService.openGenerateDocumentTemplatesModal(
                    items.map((item) => ({
                        description: item.documentRequirementType.description,
                        documentRequirementId: item.documentRequirement.id,
                        documentTypeCodes:
                            item.documentRequirementType.documentTypeCodes,
                    })),
                );
                break;

            case DocumentChecklistAction.MarkAsProvided:
            case DocumentChecklistAction.SendForSignature:
                this.documentsFacadeService.sendDocumentRequirement(
                    documentRequirementIds,
                );
                break;

            case DocumentChecklistAction.Approve:
                this.documentsFacadeService.approveDocumentRequirement(
                    documentRequirementIds,
                );
                break;

            case DocumentChecklistAction.Reject:
                this.dialogService
                    .openComponent(CommentDialogComponent, {
                        mode: 'entity',
                        title: this.translationsService.translateTokenisedString(
                            TRANSLATION_DIALOG_TITLE_REJECT_ITEM,
                        ),
                        bodyText: [
                            this.translationsService.translateTokenisedString(
                                TRANSLATION_DIALOG_COMMENT_BODY_REJECT,
                            ),
                            this.translationsService.translateTokenisedString(
                                TRANSLATION_DIALOG_MENTION_PARTIES,
                            ),
                        ],
                        entityId: items[0]?.documentRequirement.id,
                        entityType: SystemEntity.DocumentRequirement,
                    })
                    .closed.subscribe(() => {
                        this.documentsFacadeService.rejectDocumentRequirement(
                            documentRequirementIds,
                        );
                    });
                break;
            case DocumentChecklistAction.Cancel:
                this.dialogService.open({
                    dialogMode: DialogMode.CancelDocumentRequirement,
                    callback: () =>
                        this.documentsFacadeService.cancelDocumentRequirement(
                            documentRequirementIds,
                            entityType,
                            entityId,
                        ),
                });
                break;
            case DocumentChecklistAction.Delete:
                this.dialogService.open({
                    dialogMode: DialogMode.DeleteDocumentRequirement,
                    callback: () =>
                        documentRequirementIds.map((id) =>
                            this.documentsFacadeService.deleteDocumentRequirement(
                                id,
                                entityType,
                                entityId,
                            ),
                        ),
                });
                break;
            case DocumentChecklistAction.CreateExternalTask:
                documentRequirementIds.map((id, index) =>
                    this.documentsFacadeService.createDocumentExternalTask(
                        id,
                        entityType,
                        entityId,
                        items[index].documentRequirement.party.id,
                    ),
                );
                break;
        }
    }

    addDocumentsToEnvelope(envelopes: Envelope[]): void {
        this.activeDocumentChecklistItems
            .reduce(
                (accumulator, item) => [
                    ...accumulator,
                    ...item.documentRequirement.associatedDocuments,
                ],
                [],
            )
            .forEach((document) =>
                envelopes.forEach((envelope) =>
                    this.envelopeFacadeService.dispatchAddDocumentForId(
                        envelope.id,
                        document.id,
                    ),
                ),
            );
        this.activeDocumentChecklistItems = undefined;
    }

    addDocumentsToEnvelopeOpenModal(): void {
        this.modalManagementService.openModalWithId(
            ADD_TO_ENVELOPE_ACTION.modalId,
        );
    }
}
